import React, { useEffect, useMemo, useState } from "react";
import { toast } from "sonner";
import { Anchor, Users, Star, UserCheck, UserPlus, Trash2, Pencil, ImagePlus, X, Search, Heart, HeartHandshake, Briefcase } from "lucide-react";
import { api, fileUrl, formatErr } from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Textarea } from "@/components/ui/textarea";
import { Label } from "@/components/ui/label";
import { Badge } from "@/components/ui/badge";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from "@/components/ui/dialog";
import { AlertDialog, AlertDialogAction, AlertDialogCancel, AlertDialogContent, AlertDialogDescription, AlertDialogFooter, AlertDialogHeader, AlertDialogTitle } from "@/components/ui/alert-dialog";

// Roster panel — Owners / Family / Staff. The "regulars" of the yacht.
// The full profile (photo, PIN, age, gender, medical, preferences) is
// stored on ONE record and pulled into future charter guest entries
// via the "Load from regulars" picker on Trips & Guests. Medical +
// preference data are Admin/Manager-only (matches the wellness matrix).

const ROLE_TABS = [
  { key: "all",    label: "All",     icon: Users },
  { key: "owner",  label: "Owners",  icon: Star },
  { key: "family", label: "Family",  icon: HeartHandshake },
  { key: "staff",  label: "Staff",   icon: Briefcase },
];

const ROLE_STYLES = {
  owner:  { bg: "bg-amber-500/10", text: "text-amber-700 dark:text-amber-300", border: "border-amber-500/40", label: "Owner" },
  family: { bg: "bg-rose-500/10",  text: "text-rose-700 dark:text-rose-300",  border: "border-rose-500/40",  label: "Family" },
  staff:  { bg: "bg-sky-500/10",   text: "text-sky-700 dark:text-sky-300",    border: "border-sky-500/40",   label: "Staff"  },
};

const EMPTY_FORM = {
  id: null, name: "", role: "family", female: false, age: "",
  image_path: null, default_pin: "", email: "",
  allergies: "", conditions: "", medications: "", medical_history: "",
  dietary: "", favourite_treatment: "", preferred_therapist: "",
  preferences_notes: "", internal_notes: "",
};

export default function RegularsPanel() {
  const { admin } = useAuth();
  const role = admin?.role || "";
  // Only Admin / Manager / Developer may create/edit. Crew see the list.
  const canEdit = ["developer", "admin", "manager", "supervisor"].includes(role);

  const [rows, setRows] = useState([]);
  const [loading, setLoading] = useState(false);
  const [activeTab, setActiveTab] = useState("all");
  const [q, setQ] = useState("");
  const [editorOpen, setEditorOpen] = useState(false);
  const [form, setForm] = useState(EMPTY_FORM);
  const [saving, setSaving] = useState(false);
  const [deleteTarget, setDeleteTarget] = useState(null);
  const [serviceCategories, setServiceCategories] = useState([]);
  const [servicePrefs, setServicePrefs] = useState({});

  const load = async () => {
    setLoading(true);
    try {
      const { data } = await api.get("/admin/roster");
      setRows(data || []);
    } catch (e) {
      toast.error(formatErr(e.response?.data?.detail) || "Could not load regulars");
    } finally {
      setLoading(false);
    }
  };
  const loadServiceCategories = async () => {
    try {
      const { data } = await api.get("/admin/preference-categories");
      setServiceCategories((data || []).filter((c) => c.active !== false));
    } catch { setServiceCategories([]); }
  };
  useEffect(() => { load(); loadServiceCategories(); }, []);

  const filtered = useMemo(() => {
    const s = q.trim().toLowerCase();
    return rows.filter((r) => {
      if (activeTab !== "all" && r.role !== activeTab) return false;
      if (s && !(r.name || "").toLowerCase().includes(s)) return false;
      return true;
    });
  }, [rows, activeTab, q]);

  const counts = useMemo(() => {
    const c = { all: rows.length, owner: 0, family: 0, staff: 0 };
    for (const r of rows) c[r.role] = (c[r.role] || 0) + 1;
    return c;
  }, [rows]);

  const openNew = () => { setForm(EMPTY_FORM); setServicePrefs({}); setEditorOpen(true); };
  const openEdit = (row) => {
    setServicePrefs({});
    setForm({
      id: row.id, name: row.name || "", role: row.role || "family",
      female: !!row.female, age: row.age != null ? String(row.age) : "",
      image_path: row.image_path || null,
      default_pin: row.default_pin || "", email: row.email || "",
      allergies: row.allergies || "", conditions: row.conditions || "",
      medications: row.medications || "", medical_history: row.medical_history || "",
      dietary: row.dietary || "", favourite_treatment: row.favourite_treatment || "",
      preferred_therapist: row.preferred_therapist || "",
      preferences_notes: row.preferences_notes || "",
      internal_notes: row.internal_notes || "",
    });
    setEditorOpen(true);
    api.get("/admin/roster-preferences", { params: { roster_ids: row.id } })
      .then(({ data }) => {
        const next = {};
        for (const p of (data || [])) next[p.category_id] = p.text || "";
        setServicePrefs(next);
      })
      .catch(() => {});
  };

  const uploadPhoto = async (file) => {
    if (!file) return;
    const fd = new FormData();
    fd.append("file", file);
    try {
      const { data } = await api.post("/admin/upload", fd, { headers: { "Content-Type": "multipart/form-data" } });
      setForm((f) => ({ ...f, image_path: data.file_path }));
    } catch (e) {
      toast.error(formatErr(e.response?.data?.detail) || "Photo upload failed");
    }
  };

  const submit = async () => {
    if (!form.name.trim()) { toast.error("Name is required"); return; }
    setSaving(true);
    try {
      const payload = {
        ...form,
        age: form.age === "" ? null : parseInt(form.age, 10),
        default_pin: (form.default_pin || "").trim() || null,
      };
      delete payload.id;
      let saved;
      if (form.id) {
        const { data } = await api.put(`/admin/roster/${form.id}`, payload);
        saved = data;
        toast.success("Regular updated");
      } else {
        const { data } = await api.post("/admin/roster", payload);
        saved = data;
        toast.success("Regular added");
      }
      const rid = saved?.id || form.id;
      if (rid && serviceCategories.length) {
        await Promise.all(serviceCategories.map((c) => api.put("/admin/roster-preferences", {
          roster_id: rid, category_id: c.id, text: servicePrefs[c.id] || "",
        })));
      }
      setEditorOpen(false);
      await load();
    } catch (e) {
      toast.error(formatErr(e.response?.data?.detail) || "Save failed");
    } finally { setSaving(false); }
  };

  const doDelete = async () => {
    const t = deleteTarget;
    setDeleteTarget(null);
    if (!t) return;
    try {
      await api.delete(`/admin/roster/${t.id}`);
      toast.success(`${t.name} removed from regulars`);
      await load();
    } catch (e) {
      toast.error(formatErr(e.response?.data?.detail) || "Delete failed");
    }
  };

  return (
    <div className="space-y-6" data-testid="regulars-panel">
      <div className="flex flex-col sm:flex-row sm:items-end sm:justify-between gap-4">
        <div>
          <h1 className="font-display text-3xl text-primary flex items-center gap-2">
            <UserCheck className="h-6 w-6 text-accent" strokeWidth={1.4} /> Regulars
          </h1>
          <p className="text-sm text-muted-foreground mt-1 max-w-xl">
            Owners, family & staff who return often. Their photo, PIN, medical notes and preferences live here and load into every new trip in one tap — no wellness questionnaire ever shown to a regular.
          </p>
        </div>
        {canEdit && (
          <Button onClick={openNew} data-testid="regulars-add-btn" className="rounded-full">
            <UserPlus className="h-4 w-4 mr-2" /> New regular
          </Button>
        )}
      </div>

      {/* Role tabs */}
      <div className="flex flex-wrap gap-2 border-b border-border pb-1">
        {ROLE_TABS.map((t) => {
          const Icon = t.icon;
          const active = activeTab === t.key;
          const count = counts[t.key];
          return (
            <button
              key={t.key}
              type="button"
              onClick={() => setActiveTab(t.key)}
              data-testid={`regulars-tab-${t.key}`}
              className={`px-4 py-2 text-sm border-b-2 transition-colors flex items-center gap-2 ${active ? "border-accent text-accent font-medium" : "border-transparent text-muted-foreground hover:text-primary"}`}
            >
              <Icon className="h-3.5 w-3.5" strokeWidth={1.4} /> {t.label}
              {count > 0 && <span className={`text-[10px] px-1.5 py-0.5 rounded-full ${active ? "bg-accent/15" : "bg-muted"}`}>{count}</span>}
            </button>
          );
        })}
        <div className="ml-auto relative">
          <Search className="absolute left-3 top-1/2 -translate-y-1/2 h-3.5 w-3.5 text-muted-foreground" />
          <Input value={q} onChange={(e) => setQ(e.target.value)} placeholder="Search by name…" className="pl-9 w-56" data-testid="regulars-search" />
        </div>
      </div>

      {loading ? (
        <div className="text-sm text-muted-foreground py-8 text-center">Loading regulars…</div>
      ) : filtered.length === 0 ? (
        <div className="text-sm text-muted-foreground py-10 text-center border border-dashed border-border rounded-lg">
          {rows.length === 0
            ? (canEdit ? "No regulars yet — add the owner, family and long-serving staff so their profiles auto-load into every trip." : "No regulars have been added yet.")
            : "No regulars match your filters."}
        </div>
      ) : (
        <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-4">
          {filtered.map((r) => (
            <RegularCard
              key={r.id}
              row={r}
              canEdit={canEdit}
              onEdit={() => openEdit(r)}
              onDelete={() => setDeleteTarget(r)}
            />
          ))}
        </div>
      )}

      {/* Editor dialog */}
      <Dialog open={editorOpen} onOpenChange={setEditorOpen}>
        <DialogContent className="max-w-2xl max-h-[90vh] overflow-y-auto" data-testid="regulars-editor">
          <DialogHeader>
            <DialogTitle className="font-display text-2xl">{form.id ? "Edit regular" : "New regular"}</DialogTitle>
          </DialogHeader>

          {/* Identity block */}
          <div className="grid grid-cols-1 sm:grid-cols-[120px_1fr] gap-4 items-start">
            <div className="flex flex-col items-center gap-2">
              {form.image_path ? (
                <div className="relative">
                  <img src={fileUrl(form.image_path)} alt="" className="w-24 h-24 rounded-full object-cover border border-border" />
                  <button
                    type="button"
                    onClick={() => setForm((f) => ({ ...f, image_path: null }))}
                    className="absolute -top-1 -right-1 bg-background border border-border rounded-full p-1"
                    data-testid="regulars-photo-remove"
                    aria-label="Remove photo"
                  ><X className="h-3 w-3" /></button>
                </div>
              ) : (
                <label className="w-24 h-24 rounded-full bg-muted border border-dashed border-border flex items-center justify-center cursor-pointer hover:border-accent" data-testid="regulars-photo-upload">
                  <ImagePlus className="h-5 w-5 text-muted-foreground" />
                  <input type="file" accept="image/*" className="hidden" onChange={(e) => uploadPhoto(e.target.files?.[0])} />
                </label>
              )}
              <span className="text-[10px] text-muted-foreground text-center">Photo (Crew-only)</span>
            </div>
            <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
              <div>
                <Label className="text-xs">Full name *</Label>
                <Input value={form.name} onChange={(e) => setForm((f) => ({ ...f, name: e.target.value }))} data-testid="regulars-name" autoFocus />
              </div>
              <div>
                <Label className="text-xs">Category *</Label>
                <div className="flex gap-1 mt-1">
                  {["owner", "family", "staff"].map((k) => (
                    <button
                      key={k}
                      type="button"
                      onClick={() => setForm((f) => ({ ...f, role: k }))}
                      className={`flex-1 text-xs px-2 py-2 border transition-colors ${form.role === k ? `${ROLE_STYLES[k].bg} ${ROLE_STYLES[k].text} ${ROLE_STYLES[k].border} font-medium` : "border-border text-muted-foreground hover:border-primary"}`}
                      data-testid={`regulars-role-${k}`}
                    >{ROLE_STYLES[k].label}</button>
                  ))}
                </div>
              </div>
              <div>
                <Label className="text-xs">Age</Label>
                <Input type="number" min="0" max="120" value={form.age} onChange={(e) => setForm((f) => ({ ...f, age: e.target.value }))} data-testid="regulars-age" />
              </div>
              <div>
                <Label className="text-xs">Default PIN (optional)</Label>
                <Input value={form.default_pin} onChange={(e) => setForm((f) => ({ ...f, default_pin: e.target.value.replace(/\D/g, "").slice(0, 4) }))} placeholder="4 digits" data-testid="regulars-pin" />
              </div>
              <div className="sm:col-span-2 flex items-center gap-3">
                <label className="flex items-center gap-2 text-sm cursor-pointer" data-testid="regulars-female-toggle">
                  <input type="checkbox" checked={form.female} onChange={(e) => setForm((f) => ({ ...f, female: e.target.checked }))} className="accent-accent" />
                  <span>Female</span>
                </label>
                <div className="flex-1">
                  <Label className="text-xs">Email (optional)</Label>
                  <Input type="email" value={form.email} onChange={(e) => setForm((f) => ({ ...f, email: e.target.value }))} placeholder="—" data-testid="regulars-email" />
                </div>
              </div>
            </div>
          </div>

          {/* Medical (crew-only) */}
          <div className="mt-2 pt-4 border-t border-border">
            <div className="flex items-center gap-2 mb-2">
              <Heart className="h-4 w-4 text-rose-500" strokeWidth={1.4} />
              <span className="font-medium text-sm">Medical &amp; wellness · Crew-only</span>
              <Badge variant="outline" className="text-[9px] border-rose-500/40 text-rose-700 dark:text-rose-300">never shown to the guest</Badge>
            </div>
            <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
              <FieldTA label="Allergies" value={form.allergies} onChange={(v) => setForm((f) => ({ ...f, allergies: v }))} testid="regulars-allergies" />
              <FieldTA label="Conditions" value={form.conditions} onChange={(v) => setForm((f) => ({ ...f, conditions: v }))} testid="regulars-conditions" />
              <FieldTA label="Medications" value={form.medications} onChange={(v) => setForm((f) => ({ ...f, medications: v }))} testid="regulars-medications" />
              <FieldTA label="Medical history" value={form.medical_history} onChange={(v) => setForm((f) => ({ ...f, medical_history: v }))} testid="regulars-history" />
            </div>
          </div>

          {/* Preferences */}
          <div className="mt-2 pt-4 border-t border-border">
            <div className="flex items-center gap-2 mb-2">
              <Star className="h-4 w-4 text-accent" strokeWidth={1.4} />
              <span className="font-medium text-sm">Preferences · Crew-only</span>
            </div>
            <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
              <FieldTA label="Dietary" value={form.dietary} onChange={(v) => setForm((f) => ({ ...f, dietary: v }))} testid="regulars-dietary" />
              <FieldTA label="Favourite treatment" value={form.favourite_treatment} onChange={(v) => setForm((f) => ({ ...f, favourite_treatment: v }))} testid="regulars-fav-treatment" />
              <FieldTA label="Preferred therapist" value={form.preferred_therapist} onChange={(v) => setForm((f) => ({ ...f, preferred_therapist: v }))} testid="regulars-therapist" />
              <FieldTA label="Preferences notes" value={form.preferences_notes} onChange={(v) => setForm((f) => ({ ...f, preferences_notes: v }))} testid="regulars-prefs-notes" />
            </div>
            <FieldTA
              label="Internal crew notes"
              value={form.internal_notes}
              onChange={(v) => setForm((f) => ({ ...f, internal_notes: v }))}
              testid="regulars-internal-notes"
              className="mt-3"
            />
          </div>

          {/* Persistent Service module preference sheet */}
          <div className="mt-2 pt-4 border-t border-border">
            <div className="flex items-center gap-2 mb-2">
              <Users className="h-4 w-4 text-accent" strokeWidth={1.4} />
              <span className="font-medium text-sm">Service preferences · Persistent</span>
              <Badge variant="outline" className="text-[9px]">used every trip</Badge>
            </div>
            <p className="text-xs text-muted-foreground mb-3">These fields are the source used by the Service module whenever this regular is onboard. Service crew edits sync back here automatically.</p>
            {serviceCategories.length === 0 ? (
              <div className="text-xs text-muted-foreground border border-dashed border-border p-3">No Service preference categories are configured yet. Add them under Modules → Service → Preference categories.</div>
            ) : (
              <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
                {serviceCategories.map((c) => (
                  <FieldTA
                    key={c.id}
                    label={c.name}
                    value={servicePrefs[c.id] || ""}
                    onChange={(v) => setServicePrefs((p) => ({ ...p, [c.id]: v }))}
                    testid={`regulars-service-${c.id}`}
                  />
                ))}
              </div>
            )}
          </div>

          <DialogFooter className="pt-4">
            <Button variant="outline" onClick={() => setEditorOpen(false)} data-testid="regulars-cancel">Cancel</Button>
            <Button onClick={submit} disabled={saving || !form.name.trim()} data-testid="regulars-save">
              {saving ? "Saving…" : (form.id ? "Save changes" : "Add regular")}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Delete confirmation */}
      <AlertDialog open={!!deleteTarget} onOpenChange={(v) => !v && setDeleteTarget(null)}>
        <AlertDialogContent data-testid="regulars-delete-confirm">
          <AlertDialogHeader>
            <AlertDialogTitle>Remove {deleteTarget?.name || "regular"}?</AlertDialogTitle>
            <AlertDialogDescription>
              Past trips they joined will keep the profile snapshot that was copied at the time. Only the roster link is cleared, so future bookings will fall back to guest-scoped wellness data.
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel>Cancel</AlertDialogCancel>
            <AlertDialogAction onClick={doDelete} className="bg-destructive hover:bg-destructive/90" data-testid="regulars-delete-confirm-btn">Remove</AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
    </div>
  );
}

function RegularCard({ row, canEdit, onEdit, onDelete }) {
  const style = ROLE_STYLES[row.role] || ROLE_STYLES.family;
  return (
    <div className="border border-border rounded-lg p-4 flex gap-3 hover:border-accent/60 transition-colors bg-card" data-testid={`regulars-card-${row.id}`}>
      {row.image_path ? (
        <img src={fileUrl(row.image_path)} alt="" className="w-16 h-16 rounded-full object-cover border border-border flex-shrink-0" loading="lazy" />
      ) : (
        <div className="w-16 h-16 rounded-full bg-muted border border-border flex items-center justify-center flex-shrink-0">
          <Anchor className="h-5 w-5 text-muted-foreground" strokeWidth={1.4} />
        </div>
      )}
      <div className="flex-1 min-w-0">
        <div className="flex items-center gap-2 flex-wrap">
          <span className="font-medium text-sm truncate" data-testid="regulars-card-name">{row.name}</span>
          <Badge variant="outline" className={`text-[9px] ${style.text} ${style.border}`}>{style.label}</Badge>
          {row.female && <Badge variant="outline" className="text-[9px] border-pink-500/40 text-pink-700 dark:text-pink-400">F</Badge>}
          {row.age != null && <Badge variant="outline" className="text-[9px]">Age {row.age}</Badge>}
        </div>
        {(row.favourite_treatment || row.preferred_therapist) && (
          <div className="text-[11px] text-muted-foreground mt-2 space-y-0.5">
            {row.favourite_treatment && <div className="truncate">★ {row.favourite_treatment}</div>}
            {row.preferred_therapist && <div className="truncate">Therapist: {row.preferred_therapist}</div>}
          </div>
        )}
        {(row.allergies || row.conditions) && (
          <div className="text-[10px] text-rose-600 dark:text-rose-400 mt-2 truncate" title={[row.allergies, row.conditions].filter(Boolean).join(" · ")}>
            <Heart className="inline h-2.5 w-2.5 mr-1" /> {[row.allergies, row.conditions].filter(Boolean).join(" · ")}
          </div>
        )}
        {canEdit && (
          <div className="flex gap-2 mt-3">
            <Button variant="outline" size="sm" onClick={onEdit} data-testid={`regulars-edit-${row.id}`} className="h-7 px-2 text-xs">
              <Pencil className="h-3 w-3 mr-1" /> Edit
            </Button>
            <Button variant="ghost" size="sm" onClick={onDelete} data-testid={`regulars-delete-${row.id}`} className="h-7 px-2 text-xs text-destructive hover:text-destructive">
              <Trash2 className="h-3 w-3" />
            </Button>
          </div>
        )}
      </div>
    </div>
  );
}

function FieldTA({ label, value, onChange, testid, className = "" }) {
  return (
    <div className={className}>
      <Label className="text-xs">{label}</Label>
      <Textarea value={value} onChange={(e) => onChange(e.target.value)} rows={2} className="text-sm" data-testid={testid} />
    </div>
  );
}
